import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { ENV } from "@app/constants/env";
import { getToken } from "@app/helpers/authProvider";
import { FormLogo } from "@feature/types/formlogo.type";
import { CategoryType } from "@feature/types/category.type";
import { FormDDLType } from "@feature/types/formsddl.type";
import { FormGridEntity } from "@feature/types/form.grid.type";
import { FormInsertEntity } from "@feature/types/form.insert.type";
import { FormUpdateEntity } from "@feature/types/form.update.type";
import { EmoFormAllocationGridEntity } from "@feature/types/form.allocation.grid.type";
import { EmoFormFieldEntity } from "@feature/types/form.fields.entity";
import { FormFieldOptionEntity } from "@feature/types/form.field.options.entity";
import { ShowExhibitorListForSendLoginDetails } from "@feature/types/exhibitor.list.sendlogindetails.type";
import { EmoFormFieldCustomFieldOption } from "@feature/types/form.custom.form.field.options.type";
import { RowMoveDto } from "@feature/types/form.updown.type";
import { FormSubmissionData } from "@feature/types/formsubmission.grid.list.type";
import { GetFormSubmissionDataRequest } from "@feature/types/form.submission.request.type";
import { EmoFormSubmissionLogInsert, EmoFormSubmissionLogUpdate } from "@feature/types/form.submissionlog.type";
import { EmoFormSubmissionData } from "@feature/types/form.submissiondata.type";
import { 
  PreviewSendLoginDetailsRequest, 
  PreviewSendLoginDetailsResponse 
} from "@feature/types/sendlogindetails.preview.type";
import { 
  SendLoginDetailsRequest, 
  SendLoginDetailsResponse 
} from "@feature/types/sendlogindetails.send.type";
import { 
  EmailTypeEntity, 
  GetEmailTemplateByTypeRequest,
  GetEmailTemplateByTypeResponse 
} from "@feature/types/emailType.type";
import { LinkEfairsJobListDto } from "../../TrafficManagement/types/link.efairsjob.list";
import { ExhibitorDetailsForCustomFieldType } from "@feature/types/customer.for.customfield.type";
import { FormSubmissionAudit } from "@feature/types/formsubmissionaudit.type";
import { WOStandardField } from "@feature/types/wo.standardfields.type";

// Add the missing ApiResponse type definition at the top of the file after imports
type ApiResponse<T> = {
  data: T;
  success: boolean;
  message?: string;
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: ENV.BASE_API_URL +ENV.TEMPLATES_APIURL,
});

const authBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (incomingArgs, api, extraOptions) => {
  let args: string | FetchArgs = incomingArgs;

  if (typeof args === "string") {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      const existingHeaders = (args as FetchArgs).headers;
      const headers =
        existingHeaders instanceof Headers
          ? existingHeaders
          : new Headers(existingHeaders as HeadersInit);

      headers.set("Authorization", `Bearer ${token}`);
      (args as FetchArgs).headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && token) {
      // refresh token flow could go here
    }

    return result;
  } catch {
    return { error: { status: 500, data: "Token retrieval failed" } } as any;
  }
};

export const formTemplateApiSlice = createApi({
  reducerPath: "formTemplateApi",
  baseQuery: authBaseQuery,
  tagTypes: ["FormTemplate"],
  endpoints: (builder) => ({
    getAllFormLogosForDDList: builder.query<FormLogo[], void>({
      query: () => "/FormTemplate/GetAllFormLogosForDDList",
      providesTags: ["FormTemplate"],
    }),
    getCategoryTypeForDDList: builder.query<CategoryType[], void>({
      query: () => "/FormTemplate/GetCategoryTypeForDDList",
      providesTags: ["FormTemplate"],
    }),
    getFormsForDDList: builder.query<FormDDLType[], void>({
      query: () => "/FormTemplate/GetFormsForDDList",
      providesTags: ["FormTemplate"],
    }),
    getFormsTemplateList: builder.query<FormGridEntity[], void>({
      query: () => "/FormTemplate/GetAllFormTempalteList",
      providesTags: ["FormTemplate"],
    }),
    getFormsAllocationList: builder.query<FormGridEntity[], { Showid: number }>(
      {
        query: ({ Showid }) =>
          `/FormTemplate/GetAllFormAllocationList/${Showid}`,
        providesTags: ["FormTemplate"],
      },
    ),
    getGetFormTemplateByID: builder.query<FormGridEntity[], { id: number }>({
      // Fix: Use only the ID in the path, not as a query string
      query: ({ id }) => `/FormTemplate/GetFormTemplateByID/${id}`,
      providesTags: ["FormTemplate"],
    }),

    insertFormData: builder.mutation<
      ApiResponse<FormInsertEntity>,
      FormInsertEntity
    >({
      query: (formbody) => ({
        url: "FormTemplate/InsertFormData",
        method: "POST",
        body: formbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    updateFormData: builder.mutation<
      ApiResponse<FormUpdateEntity>,
      FormUpdateEntity
    >({
      query: (formbody) => ({
        url: "FormTemplate/UpdateFormData",
        method: "POST",
        body: formbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    deleteFormData: builder.mutation<
      ApiResponse<FormUpdateEntity>,
      { emoFormId: number }
    >({
      query: ({ emoFormId }) => ({
        url: `FormTemplate/DeleteFormTemplate/${emoFormId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    rowUpFormData: builder.mutation<ApiResponse<FormUpdateEntity>, RowMoveDto>({
      query: (upBody) => ({
        url: `FormTemplate/RowUpDownFormTemplate`,
        method: "POST",
        body: upBody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    rowDownFormData: builder.mutation<
      ApiResponse<FormUpdateEntity>,
      RowMoveDto
    >({
      query: (downBody) => ({
        url: `FormTemplate/RowUpDownFormTemplate`,
        method: "POST",
        body: downBody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    insertFormAllocationData: builder.mutation<
      ApiResponse<EmoFormAllocationGridEntity>,
      EmoFormAllocationGridEntity
    >({
      query: (formbody) => ({
        url: "FormTemplate/InsertFormAllocationTemplateData",
        method: "POST",
        body: formbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    updateFormAllocationData: builder.mutation<
      ApiResponse<EmoFormAllocationGridEntity>,
      EmoFormAllocationGridEntity
    >({
      query: (formbody) => ({
        url: "FormTemplate/UpdateFormAllocationTemplateData",
        method: "POST",
        body: formbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),

    getEmoFormFieldType: builder.query<FormGridEntity[], void>({
      // Fix: Remove incorrect comment and fix query
      query: () => `/FormTemplate/GetEmoFormFieldTypes`,
      providesTags: ["FormTemplate"],
    }),
    getEmoFormFieldCustomFieldTypes: builder.query<FormGridEntity[], void>({
      // Fix: Remove incorrect comment
      query: () => `/FormTemplate/GetEmoFormFieldCustomFieldTypes`,
      providesTags: ["FormTemplate"],
    }),
    getEmoFormFieldData: builder.query<
      EmoFormFieldEntity[],
      { formId: number }
    >({
      // Fix: Remove incorrect comment
      query: ({ formId }) => `/FormTemplate/GetAllFormFields/${formId}`,
      providesTags: ["FormTemplate"],
    }),
    getEmoFormFieldOptionsData: builder.query<
      FormGridEntity[],
      { formFieldId: number }
    >({
      // Fix: Remove incorrect comment
      query: ({ formFieldId }) =>
        `/FormTemplate/GetEmoFormFieldOptions/${formFieldId}`,
      providesTags: ["FormTemplate"],
    }),
    insertFormFieldsData: builder.mutation<
      ApiResponse<EmoFormFieldEntity>,
      EmoFormFieldEntity
    >({
      query: (formfieldbody) => ({
        url: "FormTemplate/InsertFormFields",
        method: "POST",
        body: formfieldbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    updateFormFieldsData: builder.mutation<
      ApiResponse<EmoFormFieldEntity>,
      EmoFormFieldEntity
    >({
      query: (formfieldbody) => ({
        url: "FormTemplate/UpdateFormFields",
        method: "POST",
        body: formfieldbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    deleteFormFieldsData: builder.mutation<
      ApiResponse<EmoFormFieldEntity>,
      { emoFormFieldId: number }
    >({
      query: ({ emoFormFieldId }) => ({
        url: `FormTemplate/DeleteFormFields/${emoFormFieldId}`,
        method: "DELETE",
        //body: formId,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    moveRowFormField: builder.mutation<
      ApiResponse<EmoFormFieldEntity>,
      RowMoveDto
    >({
      query: (updownbody) => ({
        url: `FormTemplate/RowUpDownFormfield`,
        method: "POST",
        body: updownbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),

    insertEmoFormFieldOptionData: builder.mutation<
      ApiResponse<FormFieldOptionEntity>,
      FormFieldOptionEntity
    >({
      query: (formfieldOptionsbody) => ({
        url: "FormTemplate/InsertEmoFormFieldOption",
        method: "POST",
        body: formfieldOptionsbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    updateEmoFormFieldOptionData: builder.mutation<
      ApiResponse<FormFieldOptionEntity>,
      FormFieldOptionEntity
    >({
      query: (formfieldOptionsbody) => ({
        url: "FormTemplate/UpdateEmoFormFieldOption",
        method: "POST",
        body: formfieldOptionsbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    GetShowExhibitorListForSendLoginDetails: builder.query<
      ShowExhibitorListForSendLoginDetails[],
      { showId: number; sentStatus: string }
    >({
      // Fix: Remove incorrect comment
      query: ({ showId, sentStatus }) =>
        `/FormTemplate/GetShowExhibitorListForSendLoginDetails/${showId}/${sentStatus}`,
      providesTags: ["FormTemplate"],
    }),
    getEmoCustomFormFieldOptionsData: builder.query<
      FormGridEntity[],
      { formFieldId: number }
    >({
      // Fix: Remove incorrect comment
      query: ({ formFieldId }) =>
        `/FormTemplate/GetAllEmoFormFieldCustomFieldOption/${formFieldId}`,
      providesTags: ["FormTemplate"],
    }),
    insertEmoFormFieldCustomFieldOption: builder.mutation<
      ApiResponse<EmoFormFieldCustomFieldOption>,
      EmoFormFieldCustomFieldOption
    >({
      query: (formfieldOptionsbody) => ({
        url: "FormTemplate/InsertEmoFormFieldCustomFieldOption",
        method: "POST",
        body: formfieldOptionsbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    getFormSubmissionListData: builder.mutation<
      ApiResponse<FormSubmissionData>,
      GetFormSubmissionDataRequest
    >({
      query: (formbody) => ({
        url: "FormTemplate/GetFormSubmissionList",
        method: "POST",
        body: formbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    updateEmoFormFieldCustomFieldOption: builder.mutation<
      ApiResponse<EmoFormFieldCustomFieldOption>,
      EmoFormFieldCustomFieldOption
    >({
      query: (formfieldOptionsbody) => ({
        url: "FormTemplate/UpdateEmoFormFieldCustomFieldOption",
        method: "POST",
        body: formfieldOptionsbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    insertFormSubmissionLog: builder.mutation<
      ApiResponse<EmoFormSubmissionLogInsert>,
      EmoFormSubmissionLogInsert
    >({
      query: (formbody) => ({
        url: "FormTemplate/InsertFormSubmissionLog",
        method: "POST",
        body: formbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    updateFormSubmissionLog: builder.mutation<
      ApiResponse<EmoFormSubmissionLogUpdate>,
      EmoFormSubmissionLogUpdate
    >({
      query: (formbody) => ({
        url: "FormTemplate/UpdateFormSubmissionLog",
        method: "POST",
        body: formbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    getExhibitorIDByUsername: builder.mutation<
      ApiResponse<number>,
      { username: string; showId: number }
    >({
      query: ({ username, showId }) => ({
        url: `FormTemplate/GetCustomerIDByUserName?UName=${username}&ShowId=${showId}`,
        method: "GET",
      }),
      invalidatesTags: ["FormTemplate"],
    }),
     getCustomerIDByUserName_For_CustomFields: builder.query<
      ApiResponse<ExhibitorDetailsForCustomFieldType>,
      { username: string; showId: number }
    >({
      query: ({ username, showId }) => ({
        url: `FormTemplate/GetCustomerIDByUserName_For_CustomFields?UName=${username}&ShowId=${showId}`,
        method: "GET",
      }),
      providesTags: ["FormTemplate"],
    }),
    inserFormSubmissionData: builder.mutation<
      ApiResponse<EmoFormSubmissionData>,
      EmoFormSubmissionData[]
    >({
      query: (formbody) => ({
        url: "FormTemplate/InsertFormSubmissionData",
        method: "POST",
        body: formbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
        updateFormSubmissionData: builder.mutation<
      ApiResponse<EmoFormSubmissionData>,
      EmoFormSubmissionData[]
    >({
      query: (formbody) => ({
        url: "FormTemplate/UpdateFormSubmissionData",
        method: "POST",
        body: formbody,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    getFormSubmissionDataByLogId: builder.query<
      ApiResponse<EmoFormSubmissionData[]>,
      { logId: number }
    >({
      query: ({ logId }) => ({
        url: `FormTemplate/GetSubmissionDataByLogId/${logId}`,
        method: "GET",
      }),
      providesTags: ["FormTemplate"],
    }),
     getFormSubmissionData: builder.query<
      ApiResponse<EmoFormSubmissionData[]>,
      { logId: number }
    >({
      query: ({ logId }) => ({
        url: `FormTemplate/GetFormSubmissionData/${logId}`, //this is actual data which is used for edit form submission and GetSubmissionDataByLogId is used for view form submission but both are doing same thing but we need to maintain both because in future if we want to change anything in edit form submission data then we can do that without affecting view form submission data
        method: "GET",
      }),
      providesTags: ["FormTemplate"],
    }),
    
    // Preview Send Login Details
    previewSendLoginDetails: builder.mutation<
      PreviewSendLoginDetailsResponse,
      PreviewSendLoginDetailsRequest
    >({
      query: (payload) => ({
        url: "FormTemplate/PreviewSendLoginDetails",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      }),
      transformResponse: (response: any) => {
        return response;
      },
    }),
    
    // Send Login Details
    sendLoginDetails: builder.mutation<
      SendLoginDetailsResponse,
      { model: SendLoginDetailsRequest; createdBy: number }
    >({
      query: ({ model, createdBy }) => ({
        url: `EmoTemplate/SendLoginDetailsSave/${createdBy}`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: model,
      }),
      invalidatesTags: ["FormTemplate"],
    }),
    
    // Get Email Types for dropdown
    getEmailTypes: builder.query<EmailTypeEntity[], void>({
      query: () => "/FormTemplate/GetEmailTypes",
      providesTags: ["FormTemplate"],
    }),
    
    // Get Email Template by Email Type ID
    getEmailTemplateByType: builder.query<
      GetEmailTemplateByTypeResponse,
      GetEmailTemplateByTypeRequest
    >({
      query: ({ emailTypeId }) => ({
        url: "/FormTemplate/GetEmailTemplates",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          EntryMode: "GETEmailTemplate",
          EMOEmailTypeID: emailTypeId,
        },
      }),
      transformResponse: (response: any) => {
        // Backend returns array, we take the first item
        return Array.isArray(response) && response.length > 0 ? response[0] : null;
      },
      providesTags: ["FormTemplate"],
    }),
    
    // Upload Image
    uploadImage: builder.mutation<{ blobUrl: string }, File>({
    query: (file) => {
        const formData = new FormData();

        formData.append("files", file);

        return {
            url: "/FormTemplate/uploadImage", // Change to your controller
            method: "POST",
            body: formData,
        };
    },
}),
    
    // Upload File
    uploadFile: builder.mutation<
      { blobUrl: string },
      File
    >({
      query: (file) => {
        const formData = new FormData();
        formData.append("files", file);
        return {
          url: "FormTemplate/uploadFile",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["FormTemplate"],
    }),
    GetWorkOrderForLinkFormBookings: builder.query<ApiResponse<LinkEfairsJobListDto[]>, { logId: number }>({
          query: ({ logId}) => ({
            url: `FormTemplate/GetWorkOrderForLinkFormBookings?logId=${logId}`,
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }),
          providesTags: ['FormTemplate'],
        }),
            getFormSubmissionAudit: builder.query<ApiResponse<FormSubmissionAudit[]>, { logId: number }>({
          query: ({ logId}) => ({
            url: `FormTemplate/GetFormSubmissionAudit?logId=${logId}`,
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }),
          providesTags: ['FormTemplate'],
        }),
      getAllStandardFieldsWorkOrder: builder.query<ApiResponse<WOStandardField[]>, void>({
          query: () => ({
            url: `FormTemplate/GetAllStandardFields`,
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }),
          providesTags: ['FormTemplate'],
        }),
  }),
});

export const {
  useGetAllFormLogosForDDListQuery,
  useGetCategoryTypeForDDListQuery,
  useGetFormsForDDListQuery,
  useGetFormsTemplateListQuery,
  useGetFormsAllocationListQuery,
  useGetGetFormTemplateByIDQuery,
  useInsertFormDataMutation,
  useUpdateFormDataMutation,
  useDeleteFormDataMutation,
  useRowUpFormDataMutation,
  useRowDownFormDataMutation,
  useInsertFormAllocationDataMutation,
  useUpdateFormAllocationDataMutation,
  useGetEmoFormFieldTypeQuery,
  useGetEmoFormFieldCustomFieldTypesQuery,
  useInsertFormFieldsDataMutation,
  useGetEmoFormFieldDataQuery,
  useGetEmoFormFieldOptionsDataQuery,
  useUpdateFormFieldsDataMutation,
  useDeleteFormFieldsDataMutation,
  useMoveRowFormFieldMutation,
  useInsertEmoFormFieldOptionDataMutation,
  useUpdateEmoFormFieldOptionDataMutation,
  useGetShowExhibitorListForSendLoginDetailsQuery,
  useGetEmoCustomFormFieldOptionsDataQuery,
  useInsertEmoFormFieldCustomFieldOptionMutation,
  useUpdateEmoFormFieldCustomFieldOptionMutation,
  useGetFormSubmissionListDataMutation,
  useInserFormSubmissionDataMutation,
  useGetExhibitorIDByUsernameMutation,
  useGetCustomerIDByUserName_For_CustomFieldsQuery,
  useInsertFormSubmissionLogMutation,
  useUpdateFormSubmissionLogMutation,
  useGetFormSubmissionDataByLogIdQuery,
  useGetFormSubmissionDataQuery,
  usePreviewSendLoginDetailsMutation,
  useSendLoginDetailsMutation,
  useGetEmailTypesQuery,
  useGetEmailTemplateByTypeQuery,
  useUploadImageMutation,
  useUploadFileMutation,
  useGetWorkOrderForLinkFormBookingsQuery,
  useUpdateFormSubmissionDataMutation,
  useGetFormSubmissionAuditQuery,
  useGetAllStandardFieldsWorkOrderQuery,
} = formTemplateApiSlice;
